import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { useAuthStore } from '../store/authStore';
import { getGame, updateGame, getTeam, addPlayer } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { User, Save, Check, UserPlus, Play, ArrowUp, ArrowDown, X, ScanLine, Sparkles, Loader2, AlertTriangle } from 'lucide-react';
import { cn } from '../lib/utils';
import { ScanLineupDialog } from '../components/shared/ScanLineupDialog';
import QuickTipsButton, { LINEUP_TIPS } from '../components/shared/QuickTipsButton';

const API = process.env.REACT_APP_BACKEND_URL;

// Player in lineup slot - clickable to select/reorder
const LineupSlot = ({ player, index, isSelected, onSelect, onMoveUp, onMoveDown, onRemove, canMoveUp, canMoveDown }) => {
  return (
    <div
      data-testid={`lineup-slot-${index + 1}`}
      className={cn(
        'flex items-center gap-3 bg-white border-2 rounded-lg p-3 shadow-sm transition-all cursor-pointer',
        isSelected ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-green-400'
      )}
      onClick={() => onSelect(player.id)}
    >
      <div 
        className="w-8 h-8 rounded-full bg-green-900 text-white flex items-center justify-center font-bold text-sm"
        style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
      >
        {index + 1}
      </div>
      <div className="w-10 h-10 rounded-lg bg-green-800 text-white flex items-center justify-center font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        {player.jersey_number}
      </div>
      <div className="flex-1">
        <p className="font-semibold text-stone-900">{player.name}</p>
        {player.position && (
          <p className="text-xs text-stone-500 uppercase">{player.position}</p>
        )}
      </div>
      {player.is_pitcher && (
        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded font-semibold">P</span>
      )}
      
      {/* Reorder buttons */}
      <div className="flex flex-col gap-1">
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0"
          onClick={(e) => { e.stopPropagation(); onMoveUp(index); }}
          disabled={!canMoveUp}
        >
          <ArrowUp size={14} />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 w-6 p-0"
          onClick={(e) => { e.stopPropagation(); onMoveDown(index); }}
          disabled={!canMoveDown}
        >
          <ArrowDown size={14} />
        </Button>
      </div>
      
      <Button
        size="sm"
        variant="ghost"
        className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
        onClick={(e) => { e.stopPropagation(); onRemove(player.id); }}
      >
        <X size={16} />
      </Button>
    </div>
  );
};

// Bench player - click to add
const BenchPlayer = ({ player, onAdd }) => {
  return (
    <button
      data-testid={`bench-player-${player.id}`}
      onClick={() => onAdd(player)}
      className="w-full flex items-center gap-3 p-3 rounded-lg bg-white border-2 border-stone-200 hover:border-green-500 hover:bg-green-50 transition-all text-left"
    >
      <div className="w-10 h-10 rounded-lg bg-stone-600 text-white flex items-center justify-center font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
        {player.jersey_number}
      </div>
      <div className="flex-1">
        <p className="font-semibold text-stone-800">{player.name}</p>
      </div>
      {player.is_pitcher && (
        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded font-semibold">P</span>
      )}
      <UserPlus size={18} className="text-green-600" />
    </button>
  );
};

export default function LineupPage() {
  const navigate = useNavigate();
  const { currentGameId, currentTeamId } = useGameStore();
  const { token } = useAuthStore();
  const [game, setGame] = useState(null);
  const [team, setTeam] = useState(null);
  const [battingOrder, setBattingOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lineupSet, setLineupSet] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [addPlayerDialog, setAddPlayerDialog] = useState({ open: false, player: null });
  const [scanOpen, setScanOpen] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAiDialog, setShowAiDialog] = useState(false);
  const [validatorWarn, setValidatorWarn] = useState({ open: false, issues: [] });

  // Validate softball DP/FLEX lineup before starting the game.
  // Returns { issues: [{level: 'warn'|'info', text: string}, ...] }.
  const validateLineup = () => {
    const issues = [];
    if (!battingOrder || battingOrder.length === 0) return { issues };
    if (team?.sport_type === 'softball' && team?.dp_flex_enabled) {
      const positions = battingOrder.map(p => (p?.position || '').toUpperCase());
      const hasDP = positions.includes('DP');
      const hasFLEX = positions.includes('FLEX');
      if (!hasDP) {
        issues.push({ level: 'warn', text: 'No DP (Designated Player) is set. With DP/FLEX enabled, you usually have one.' });
      }
      if (!hasFLEX) {
        issues.push({ level: 'warn', text: 'No FLEX is set. With DP/FLEX enabled, you usually have one.' });
      }
      // Duplicate position check (excluding empty / DP / FLEX which are unique-by-rule)
      const seen = {};
      positions.forEach((pos, idx) => {
        if (!pos || pos === 'DH' || pos === 'EH') return;
        if (seen[pos] !== undefined) {
          const a = battingOrder[seen[pos]]?.name || '#?';
          const b = battingOrder[idx]?.name || '#?';
          issues.push({
            level: 'warn',
            text: `Two players assigned to ${pos}: ${a} (slot ${seen[pos] + 1}) and ${b} (slot ${idx + 1}).`,
          });
        } else {
          seen[pos] = idx;
        }
      });
    }
    return { issues };
  };

  const handleStartScoring = async () => {
    const { issues } = validateLineup();
    if (issues.length > 0) {
      setValidatorWarn({ open: true, issues });
      return;
    }
    navigate('/game-mode');
  };

  useEffect(() => {
    if (!currentGameId || !currentTeamId) {
      navigate('/');
      return;
    }
    loadData();
  }, [currentGameId, currentTeamId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [gameData, teamData] = await Promise.all([
        getGame(currentGameId),
        getTeam(currentTeamId)
      ]);
      setGame(gameData);
      setTeam(teamData);
      
      if (gameData.batting_order?.length > 0) {
        const orderedPlayers = gameData.batting_order
          .map(id => teamData.players?.find(p => p.id === id))
          .filter(Boolean);
        setBattingOrder(orderedPlayers);
        setLineupSet(true);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load game data');
    } finally {
      setLoading(false);
    }
  };

  // Move player up in order
  const handleMoveUp = (index) => {
    if (index <= 0) return;
    const newOrder = [...battingOrder];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setBattingOrder(newOrder);
  };

  // Move player down in order
  const handleMoveDown = (index) => {
    if (index >= battingOrder.length - 1) return;
    const newOrder = [...battingOrder];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setBattingOrder(newOrder);
  };

  // Click on bench player - add directly for first 9, confirm for 10+
  const handleBenchPlayerClick = (player) => {
    if (battingOrder.length < 9) {
      handleDirectAdd(player);
    } else {
      setAddPlayerDialog({ open: true, player });
    }
  };

  // Direct add without confirmation
  const handleDirectAdd = async (player) => {
    if (battingOrder.find(p => p.id === player.id)) {
      toast.error('Player already in lineup');
      return;
    }

    const newBattingOrder = [...battingOrder, player];
    setBattingOrder(newBattingOrder);
    
    if (lineupSet) {
      try {
        await updateGame(currentGameId, {
          batting_order: newBattingOrder.map(p => p.id)
        });
      } catch (error) {
        console.error('Save failed:', error);
      }
    }
  };

  // Confirm adding player (for 10+)
  const handleConfirmAddPlayer = async () => {
    const player = addPlayerDialog.player;
    if (!player) return;

    await handleDirectAdd(player);
    toast.success(`${player.name} added at #${battingOrder.length + 1}`);
    setAddPlayerDialog({ open: false, player: null });
  };

  // Handle scanned lineup - create players and add to batting order + field positions
  const handleScannedPlayers = async (players) => {
    try {
      const newOrder = [...battingOrder];
      const fieldPositions = [];
      for (const p of players) {
        // Check if player already exists in roster by name (case-insensitive)
        const existing = team?.players?.find(tp => tp.name.toLowerCase() === p.name.toLowerCase());
        let player;
        if (existing) {
          player = existing;
          if (!newOrder.find(bo => bo.id === existing.id)) {
            newOrder.push(existing);
          }
        } else {
          // Create new player in roster with position
          const created = await addPlayer(currentTeamId, {
            name: p.name,
            jersey_number: p.jersey_number || undefined,
            position: p.position || undefined
          });
          player = created;
          newOrder.push(created);
        }
        // Track field position if detected
        if (p.position && player) {
          const posMap = { 'C': 'C', '1B': '1B', '2B': '2B', '3B': '3B', 'SS': 'SS', 'LF': 'LF', 'CF': 'CF', 'RF': 'RF', 'P': 'P', 'DH': 'DH', 'DP': 'DP', 'EH': 'EH', 'FLEX': 'FLEX' };
          const normalized = posMap[p.position.toUpperCase()] || p.position.toUpperCase();
          fieldPositions.push({ position: normalized, player_id: player.id, player_name: player.name || p.name });
        }
      }
      setBattingOrder(newOrder);
      // Reload team data to get new players
      const freshTeam = await getTeam(currentTeamId);
      setTeam(freshTeam);
      // Auto-fill field positions if any were detected
      if (fieldPositions.length > 0 && currentGameId) {
        try {
          await updateGame(currentGameId, {
            field_positions: [{ inning: 1, positions: fieldPositions }]
          });
          toast.success(`${fieldPositions.length} field positions auto-filled!`);
        } catch {}
      }
    } catch (err) {
      toast.error('Error adding scanned players');
    }
  };


  // Remove player from lineup
  const handleRemoveFromLineup = async (playerId) => {
    const newBattingOrder = battingOrder.filter(p => p.id !== playerId);
    setBattingOrder(newBattingOrder);
    
    if (lineupSet && newBattingOrder.length >= 9) {
      try {
        await updateGame(currentGameId, {
          batting_order: newBattingOrder.map(p => p.id)
        });
        toast.success('Player removed from lineup');
      } catch (error) {
        toast.error('Failed to save lineup change');
      }
    }
  };

  // Set Lineup button
  const handleSetLineup = async () => {
    if (battingOrder.length < 9) {
      toast.error('Please add at least 9 players to the batting order');
      return;
    }
    try {
      setSaving(true);
      await updateGame(currentGameId, {
        batting_order: battingOrder.map(p => p.id)
      });
      setLineupSet(true);
      toast.success('Lineup set! Ready to play.');
    } catch (error) {
      toast.error('Failed to save lineup');
    } finally {
      setSaving(false);
    }
  };

  // Quick save
  const handleQuickSave = async () => {
    if (battingOrder.length < 9) return;
    try {
      setSaving(true);
      await updateGame(currentGameId, {
        batting_order: battingOrder.map(p => p.id)
      });
      toast.success('Lineup saved!');
    } catch (error) {
      console.error('Quick save failed:', error);
    } finally {
      setSaving(false);
    }
  };

  // AI Suggest Lineup
  const handleAiSuggest = async () => {
    if (!currentTeamId) return;
    setAiLoading(true);
    try {
      const res = await fetch(`${API}/api/lineup/suggest/${currentTeamId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to get AI suggestion');
      }
      const data = await res.json();
      setAiSuggestion(data);
      setShowAiDialog(true);
    } catch (error) {
      toast.error(error.message || 'AI suggestion failed');
    } finally {
      setAiLoading(false);
    }
  };

  // Apply AI suggested lineup
  const handleApplyAiLineup = () => {
    if (!aiSuggestion?.suggested_order || !team?.players) return;
    const orderedPlayers = aiSuggestion.suggested_order
      .map(id => team.players.find(p => p.id === id))
      .filter(Boolean);
    if (orderedPlayers.length > 0) {
      setBattingOrder(orderedPlayers);
      setShowAiDialog(false);
      toast.success(`AI lineup applied! ${orderedPlayers.length} players set.`);
    }
  };

  const benchPlayers = team?.players?.filter(
    p => !battingOrder.find(bp => bp.id === p.id)
  ) || [];

  const canSetLineup = battingOrder.length >= 9;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-100">
        <div className="w-12 h-12 border-4 border-green-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 p-4 md:p-8" data-testid="lineup-page">
      <QuickTipsButton tips={LINEUP_TIPS} storageKey="ds_quicktips_seen_lineup_v1" />
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-green-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              BATTING LINEUP
            </h1>
            <p className="text-stone-600">
              {game?.is_home ? 'vs' : '@'} {game?.opponent_name} • {game?.date}
            </p>
            <p className="text-sm text-stone-500 mt-1">Tap players to add/reorder • Use arrows to move up/down</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleAiSuggest} disabled={aiLoading || !team?.players?.length}
              variant="outline" className="border-amber-400 text-amber-700 hover:bg-amber-50" data-testid="ai-suggest-lineup-btn">
              {aiLoading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Sparkles size={16} className="mr-2" />}
              {aiLoading ? 'Thinking...' : 'AI Lineup'}
            </Button>
            <Button onClick={() => setScanOpen(true)} variant="outline" className="border-green-400 text-green-700 hover:bg-green-50" data-testid="scan-lineup-btn">
              <ScanLine size={16} className="mr-2" /> Scan Card
            </Button>
            {!lineupSet ? (
              <Button 
                onClick={handleSetLineup}
                disabled={saving || !canSetLineup}
                data-testid="set-lineup-btn"
                className={cn(
                  "text-lg px-6",
                  canSetLineup 
                    ? "bg-green-600 hover:bg-green-500 animate-pulse" 
                    : "bg-stone-400"
                )}
              >
                <Check size={20} className="mr-2" />
                {saving ? 'Setting...' : 'Set Lineup'}
              </Button>
            ) : (
              <>
                <Button 
                  onClick={handleQuickSave}
                  disabled={saving}
                  variant="outline"
                  data-testid="save-changes-btn"
                >
                  <Save size={16} className="mr-1" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  onClick={handleStartScoring}
                  data-testid="go-to-scoring-btn"
                  className="bg-green-600 hover:bg-green-500 text-lg px-6"
                >
                  <Play size={20} className="mr-2" />
                  Start Scoring
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Lineup Set Banner */}
        {lineupSet && (
          <div className="bg-green-100 border border-green-300 rounded-lg p-3 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Check className="text-green-600" size={20} />
              <span className="text-green-800 font-semibold">Lineup is set!</span>
              <span className="text-green-600 text-sm">You can still make changes during the game.</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Batting Order */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  BATTING ORDER ({battingOrder.length}/9{battingOrder.length > 9 ? '+' : ''})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {battingOrder.length === 0 ? (
                    <div className="text-center py-12 text-stone-500 border-2 border-dashed border-stone-300 rounded-lg">
                      <User size={32} className="mx-auto mb-2 opacity-50" />
                      <p className="mb-3">Tap players from the bench to build your lineup</p>
                      <Button onClick={() => setScanOpen(true)} variant="outline" className="border-green-400 text-green-700 hover:bg-green-50" data-testid="scan-lineup-empty-btn">
                        <ScanLine size={16} className="mr-2" /> Scan Lineup Card
                      </Button>
                    </div>
                  ) : (
                    battingOrder.map((player, index) => (
                      <LineupSlot
                        key={player.id}
                        player={player}
                        index={index}
                        isSelected={selectedPlayerId === player.id}
                        onSelect={setSelectedPlayerId}
                        onMoveUp={handleMoveUp}
                        onMoveDown={handleMoveDown}
                        onRemove={handleRemoveFromLineup}
                        canMoveUp={index > 0}
                        canMoveDown={index < battingOrder.length - 1}
                      />
                    ))
                  )}
                  
                  {/* Empty slots for first 9 positions */}
                  {Array.from({ length: Math.max(0, 9 - battingOrder.length) }).map((_, i) => (
                    <div
                      key={`empty-${i}`}
                      className="flex items-center gap-3 border-2 border-dashed border-stone-300 rounded-lg p-3 text-stone-400"
                    >
                      <div className="w-8 h-8 rounded-full border-2 border-dashed border-stone-300 flex items-center justify-center text-sm font-bold">
                        {battingOrder.length + i + 1}
                      </div>
                      <span className="text-sm">Tap a player from bench to add</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bench */}
          <div>
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  BENCH ({benchPlayers.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {benchPlayers.length === 0 ? (
                  <p className="text-center text-stone-500 py-4">All players in lineup</p>
                ) : (
                  <div className="space-y-2">
                    {benchPlayers.map((player) => (
                      <BenchPlayer
                        key={player.id}
                        player={player}
                        onAdd={handleBenchPlayerClick}
                      />
                    ))}
                  </div>
                )}
                
                {lineupSet && benchPlayers.length > 0 && (
                  <p className="text-xs text-stone-400 mt-4 text-center">
                    Tap a player to add them to the lineup
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Add Player Confirmation Dialog (for 10+) */}
      <Dialog open={addPlayerDialog.open} onOpenChange={(open) => setAddPlayerDialog({ open, player: addPlayerDialog.player })}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus size={20} className="text-green-600" />
              Add to Batting Lineup?
            </DialogTitle>
            <DialogDescription>
              Add <span className="font-semibold text-stone-900">{addPlayerDialog.player?.name}</span> to the batting lineup?
              <span className="block mt-2 text-amber-600">
                They will bat in position #{battingOrder.length + 1}
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setAddPlayerDialog({ open: false, player: null })}
            >
              No
            </Button>
            <Button
              className="flex-1 bg-green-600 hover:bg-green-500"
              onClick={handleConfirmAddPlayer}
              data-testid="confirm-add-player-btn"
            >
              Yes, Add
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ScanLineupDialog open={scanOpen} onOpenChange={setScanOpen} onPlayersScanned={handleScannedPlayers} />

      {/* AI Lineup Suggestion Dialog */}
      <Dialog open={showAiDialog} onOpenChange={setShowAiDialog}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles size={20} className="text-amber-500" />
              AI Suggested Lineup
            </DialogTitle>
            <DialogDescription>
              {aiSuggestion?.reasoning || 'AI-generated batting order based on player stats.'}
            </DialogDescription>
          </DialogHeader>
          {aiSuggestion && (
            <div className="space-y-2 mt-2">
              {aiSuggestion.order_names?.map((name, i) => (
                <div key={i} className="flex items-start gap-3 p-2 rounded-lg bg-stone-50 border border-stone-200">
                  <div className="w-7 h-7 rounded-full bg-amber-600 text-white flex items-center justify-center font-bold text-xs shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-stone-900 text-sm">{name}</p>
                    {aiSuggestion.slot_notes?.[String(i + 1)] && (
                      <p className="text-xs text-stone-500 mt-0.5">{aiSuggestion.slot_notes[String(i + 1)]}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowAiDialog(false)}>
              Cancel
            </Button>
            <Button className="flex-1 bg-amber-600 hover:bg-amber-500" onClick={handleApplyAiLineup} data-testid="apply-ai-lineup-btn">
              <Check size={16} className="mr-2" /> Apply Lineup
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Softball DP/FLEX Lineup Validator Warning */}
      <Dialog open={validatorWarn.open} onOpenChange={(open) => setValidatorWarn({ ...validatorWarn, open })}>
        <DialogContent className="bg-white max-w-md" data-testid="lineup-validator-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-700 flex items-center gap-2">
              <AlertTriangle size={18} /> Lineup Check
            </DialogTitle>
            <DialogDescription className="text-stone-600">
              Quick double-check before you start - you can still continue if these are intentional.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {validatorWarn.issues.map((iss, i) => (
              <div
                key={i}
                className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3"
                data-testid={`validator-issue-${i}`}
              >
                <AlertTriangle size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-stone-800">{iss.text}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setValidatorWarn({ open: false, issues: [] })}
              data-testid="validator-go-back-btn"
            >
              Go back &amp; fix
            </Button>
            <Button
              className="flex-1 bg-green-700 hover:bg-green-600 text-white"
              onClick={() => { setValidatorWarn({ open: false, issues: [] }); navigate('/game-mode'); }}
              data-testid="validator-continue-btn"
            >
              Start anyway
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
